/**
 * AttachmentsInput component allows users to upload and manage file attachments.
 * 
 * @remarks
 * This component can be used before or after ticket creation.
 * When used before ticket creation, it collects files but doesn't upload them.
 * When ticketId is provided, it enables immediate upload to the server.
 * 
 * @param control - The control object from react-hook-form.
 * @param name - The name of the field in the form.
 * @param ticketId - Optional ID of the ticket for immediate uploads.
 * 
 * @returns A React component for file upload.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Controller, Control } from 'react-hook-form';
import { Upload, UploadFileInfo } from '@progress/kendo-react-upload';
import { TicketEasyService } from '../../../../utils/ServiceLocator';
import { getToken } from '../../../../utils/AdalConfig';
import { useParams } from 'react-router';
import { Typography, Box } from '@mui/material';

interface AttachmentsInputProps {
  control: Control<any>;
  name: string;
  ticketId?: string;
  sx?: React.CSSProperties;
}

const AttachmentsInput: React.FC<AttachmentsInputProps> = ({ control, name, ticketId, sx }) => {
  const params = useParams();
  const effectiveTicketId = ticketId || params.Id;
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [uploadEnabled, setUploadEnabled] = useState<boolean>(!!effectiveTicketId);
  const uploadRef = useRef<any>(null);
  
  useEffect(() => {
    setUploadEnabled(!!effectiveTicketId);
  }, [effectiveTicketId]);  

  return (
    <div style={sx}>
      {!uploadEnabled && (
        <Box sx={{ mb: 1, color: '#666' }}>
          <Typography variant="caption">
            Files will be uploaded after ticket creation
          </Typography>
        </Box>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={[]}        
        render={({ field }) => (
          <Upload
            {...field}
            ref={uploadRef}
            batch={false}
            multiple={true}
            defaultFiles={[]}
            withCredentials={false}
            autoUpload={uploadEnabled}
            saveUrl={uploadEnabled ? `${TicketEasyService}DocumentUploadEndpoint/DocumentUpload/${effectiveTicketId}` : ""}
            saveHeaders={uploadEnabled ? {
              'Authorization': 'Bearer ' + getToken()
            } : {}}
            saveField="file"
            onAdd={(e) => {
              if (!uploadEnabled) {
                const newFiles: UploadFileInfo[] = e.affectedFiles.map((file: any) => file);
                setFiles([...files, ...newFiles]);
                field.onChange([...field.value, ...newFiles]);
                return false;
              }
              const newFiles: UploadFileInfo[] = e.affectedFiles.map((file: any) => file);
              setFiles([...files, ...newFiles]);
              field.onChange([...field.value, ...newFiles]);
            }}
            onRemove={(e) => {
              const removedFiles: UploadFileInfo[] = e.affectedFiles.map((file: any) => file);
              const remainingFiles = files.filter((file: UploadFileInfo) => !removedFiles.includes(file));
              setFiles(remainingFiles);
              field.onChange(field.value.filter((file: UploadFileInfo) => !removedFiles.includes(file)));
            }}
            onProgress={(e) => {
              if (!uploadEnabled) {
                return false;
              }
            }}
            onStatusChange={(e) => {
              if (!uploadEnabled) {
                return false;
              }
            }}
          />
        )}
      />    
    </div>
  );
};

export default AttachmentsInput;